import React, { useState } from 'react';
import { TextField, Box, Typography, Radio, RadioGroup, FormControlLabel, FormLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import ProceedButton from '../../Components/Buttons/ProceedButton';
import { useAuth } from '../../Context/AuthContext';
import { FOODIMETRIC_HOST_URL } from '../../Utils/host';
import { Helmet } from 'react-helmet-async';

const CustomTextField = styled(TextField)({
    '& label.Mui-focused': {
        color: '#3a8589',
    },
    '& .MuiInput-underline:after': {
        borderBottomColor: '#3a8589',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#3a8589',
        },
        '&:hover fieldset': {
            borderColor: '#3a8589',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#3a8589',
        },
    },
});

const CustomRadio = styled(Radio)({
    color: '#3a8589',
    '&.Mui-checked': {
        color: '#3a8589',
    },
});

const WHR = () => {
    const { user, logout } = useAuth();
    const [waist, setWaist] = useState('');
    const [hip, setHip] = useState('');
    const [gender, setGender] = useState('male');
    const [whr, setWhr] = useState(null);

    const handleWaistChange = (e) => setWaist(e.target.value);
    const handleHipChange = (e) => setHip(e.target.value);
    const handleGenderChange = (e) => setGender(e.target.value);

    const calculateWHR = () => {
        const whrValue = (waist / hip).toFixed(2);
        setWhr(whrValue);
        return whrValue;
    };

    const handleProceed = async () => {
        const whr_value = calculateWHR();
        if (!whr_value) return;

        const calculationPayload = {
            user_id: user._id,
            calculator_name: "WHR",
            parameters: {
                waist: `${waist} cm`,
                hip: `${hip} cm`,
                gender: gender,
            },
            result: whr_value,
            calculation_details: "WHR calculated using waist and hip circumferences in cm",
        };

        try {
            const response = await fetch(`${FOODIMETRIC_HOST_URL}/calculations`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,
                },
                body: JSON.stringify(calculationPayload),
            });

            if (response.status === 401) {
                logout()
                window.location.href = "/login";
                return; // Prevent further code execution
            }

            if (response.ok) {
                await response.json();
            } else {
                const error = await response.json();
                console.error("Failed to save calculation:", error);
            }
        } catch (err) {
            console.error("Error saving calculation:", err);
        }
    };

    return (
        <main className="py-8">
            <Helmet>
                <title>WHR Calculator - Check Your Waist-to-Hip Ratio | Foodimetric</title>
                <meta name="description" content="Find out your Waist-to-Hip Ratio (WHR) using our accurate WHR calculator. Learn if you're at risk for obesity-related diseases today!" />
                <meta name="keywords" content="WHR rechner, waist-to-hip-ratio rechner, waist to hip ratio rechner, whr calculator, WHR calculator, waist-to-hip ratio, body fat distribution, health risk assessment, obesity risk, weight management, fitness tracking, Nigerian health tools, Foodimetric WHR, whr medical tool, abdominal fat risk, waist to hip health risk" />
                <script type="application/ld+json">
                    {`{
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "WHR Calculator",
    "description": "Find out your Waist-to-Hip Ratio (WHR) using our accurate WHR calculator. Learn if you're at risk for obesity-related diseases today!",
    "url": "https://www.foodimetric.com/anthro/WHR"
  }`}
                </script>

            </Helmet>

            <div className="bg-white p-8 min-h-screen">
                <Box
                    sx={{
                        maxWidth: { xs: '100%' },
                        mx: 'auto',
                        px: { xs: 2, sm: 4 },
                        py: 4,
                        textAlign: 'center',
                        fontFamily: 'Outfit, serif',
                    }}
                >
                    <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontFamily: 'Outfit, serif' }}>
                        WHR Calculator – Waist-to-Hip Ratio Health Tool
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 2, fontSize: { xs: '0.95rem', sm: '1rem' }, textAlign: 'left', fontFamily: 'Outfit, serif' }}>
                        The Waist-to-Hip Ratio (WHR) is a key indicator of fat distribution in your body. It helps assess your risk of heart disease, obesity, and metabolic issues. Use this free WHR calculator to determine whether your body shape puts you at risk.
                    </Typography>

                    <CustomTextField label="Waist (cm)" type="number" value={waist} onChange={handleWaistChange} fullWidth margin="normal" sx={{ fontFamily: 'Outfit, serif' }} />
                    <CustomTextField label="Hip (cm)" type="number" value={hip} onChange={handleHipChange} fullWidth margin="normal" sx={{ fontFamily: 'Outfit, serif' }} />

                    <Box sx={{ textAlign: 'left', mt: 2 }}>
                        <FormLabel component="legend" sx={{ fontFamily: 'Outfit, serif' }}>Gender</FormLabel>
                        <RadioGroup row value={gender} onChange={handleGenderChange} aria-label="gender" name="gender">
                            <FormControlLabel value="male" control={<CustomRadio />} label="Male" sx={{ fontFamily: 'Outfit, serif' }} />
                            <FormControlLabel value="female" control={<CustomRadio />} label="Female" sx={{ fontFamily: 'Outfit, serif' }} />
                        </RadioGroup>
                    </Box>

                    <ProceedButton color="#ffba08" type="button" auth="authorized" onClick={handleProceed} sx={{ fontFamily: 'Outfit, serif' }} />

                    {whr !== null && (
                        <Typography variant="h6" sx={{ mt: 4, fontFamily: 'Outfit, serif' }}>
                            Your Waist-to-Hip Ratio is: {whr}
                        </Typography>
                    )}

                    <Box sx={{ mt: 6, textAlign: 'left', fontFamily: 'Outfit, serif' }}>
                        <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            What is Waist-to-Hip Ratio (WHR)?
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Outfit, serif' }}>
                            WHR is a measurement that compares the circumference of your waist to that of your hips. It helps assess fat distribution and potential health risks such as cardiovascular disease, type 2 diabetes, and stroke.
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 4, fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            How to Use This Calculator
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Outfit, serif' }}>
                            To use our calculator, you’ll need a tape measure:
                            <ul>
                                <li>Measure your waist circumference at the narrowest point, usually just above the belly button.</li>
                                <li>Measure your hip circumference at the widest part of your hips or buttocks.</li>
                                <li>
                                    Enter both values in centimeters, select your gender, and click{' '}
                                    <span style={{ color: '#ffba08', fontWeight: 'bold' }}>Proceed</span> to get your result.
                                </li>
                            </ul>
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 4, fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            WHR Risk Categories by Gender
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Outfit, serif' }}>
                            <ul>
                                <li>Men: Low risk (WHR &lt; 0.90), Moderate risk (0.90–0.99), High risk (&ge; 1.0)</li>
                                <li>Women: Low risk (WHR &lt; 0.85), Moderate risk (0.85–0.89), High risk (&ge; 0.90)</li>
                            </ul>
                        </Typography>

                        <Typography variant="h5" sx={{ mt: 4, fontSize: { xs: '1.2rem', sm: '1.5rem' }, fontWeight: 'bold', fontFamily: 'Outfit, serif' }}>
                            WHR vs BMI: What's the Difference?
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Outfit, serif' }}>
                            While BMI measures overall body fat based on weight and height, WHR focuses on fat distribution. A person can have a normal BMI but still be at risk if their WHR is high.
                        </Typography>

                        <Typography sx={{ fontFamily: 'Itim, cursive', mt: 4 }}>
                            Source:{' '}
                            <a href="https://www.who.int/news-room/fact-sheets/detail/obesity-and-overweight" target="_blank" rel="noreferrer" style={{ color: '#3a8589' }}>
                                World Health Organization. Obesity and overweight. 2021.
                            </a>
                        </Typography>
                    </Box>
                </Box>
            </div>
        </main>
    );
};

export default WHR;
